import React, { useMemo } from 'react';

import AnomalyDescription from '../report-items/anomalyDescription';
import Conditional from '../../../common/conditional';
import CustomSection from '../../custom-general/custom-section';
import PDFTablePage from '../../../themed/pdf/pdfTablePage';

import { concatClassNames, formatNumberRange, getAnomalyClassStyle, getAnomalyFieldColor, getAnomalyTypeClassFromAnomaly, getAnomalyTypeStyle, getChunkedAnomalySummaryItems, getIsAnomalyType, getIsAnomalyWithCMD, getMaxAnomalyType, getMaxClassFromInspectedItem, renderCell, renderStyle } from '../../../../utils/utilities';
import { Column, FVIEquipmentItem } from '../../../../utils/models';

import classNames from './style.module.scss';
import useProjectDetails from '../../../../hooks/useProjectDetails';

interface EquipmentAnomalySummaryItemProps {
    equipmentItem: FVIEquipmentItem;
    includeSeparator: boolean;
	sectionTitle: string;
	sectionSubtitle?: string;
}

interface EquipmentAnomalyRowProps {
	columns: Column[];
	equipmentItem: FVIEquipmentItem;
}

const EquipmentAnomalyRow: React.FC<EquipmentAnomalyRowProps> = ({ columns, equipmentItem}) => {
	return (
		<div className="my-4 table-responsive">
			<table>
				<thead>
					<tr>
						{columns.map((column) => (
							<th key={column.path}>{column.label}</th>
						))}
					</tr>
				</thead>
				<tbody>
					<tr>
						{columns.map((column: Column) => (
							<td key={column.path} style={renderStyle(equipmentItem, column)}>
								{renderCell(equipmentItem, column)}
							</td>
						))}
					</tr>
				</tbody>
			</table>
		</div>
	)
};

const EquipmentAnomalySummaryItem: React.FC<EquipmentAnomalySummaryItemProps> = ({ equipmentItem, includeSeparator, sectionTitle, sectionSubtitle }) => {
	const { projectDetails } = useProjectDetails();

	const isAnomalyType = useMemo(() => {
		return projectDetails && getIsAnomalyType(projectDetails);
	}, [projectDetails]);
	
	const isAnomalyWithCMD = useMemo(() => {
		return projectDetails && getIsAnomalyWithCMD(projectDetails);
	}, [projectDetails]);

	const anomalySummaryColumnsFirst: Column[] = useMemo(() => {
		return [
			(isAnomalyType || isAnomalyWithCMD)
			? {
				path: 'class',
				label: `${isAnomalyWithCMD ? 'Condition' : 'Anomaly'} Class`,
				content: (item: FVIEquipmentItem) => <span>{getAnomalyTypeClassFromAnomaly(getMaxAnomalyType(item))}</span>,
				style: (item: FVIEquipmentItem) => getAnomalyTypeStyle({ class: getMaxAnomalyType(item) })
			  } 
			: {
				path: 'class',
				label: 'Damage Class',
				content: (item: FVIEquipmentItem) => <span>{getMaxClassFromInspectedItem(item)}</span>,
				style: (item: FVIEquipmentItem) => getAnomalyClassStyle({ class: getMaxClassFromInspectedItem(item)})
			},
			{
				path: 'anomalySummary.pipeBody',
				label: 'Body Condition',
				style: (item) => getAnomalyFieldColor(item.anomalySummary.pipeBody)
			},
			{
				path: 'anomalySummary.noOfPoints',
				label: 'No. of Anomaly In Body',
			},
			{
				path: 'anomalySummary.pitting',
				label: 'Pitting Condition',
				style: (item) => getAnomalyFieldColor(item.anomalySummary.pitting)
			},
			{
				path: 'anomalySummary.noOfPittingLocations',
				label: 'No. of Pitting Locations',
			},
		];
	}, [isAnomalyType, isAnomalyWithCMD]);

	const paginatedAnomalyItems = useMemo(() => {
		return getChunkedAnomalySummaryItems(equipmentItem);
	}, [equipmentItem]);

    const classStyle = getAnomalyClassStyle({ class: getMaxClassFromInspectedItem(equipmentItem)});

    return (
		<React.Fragment>
			<Conditional isVisible={paginatedAnomalyItems.length > 0}>
				{paginatedAnomalyItems.map((anomalyItems, index) => (
					<React.Fragment key={index}>
						<Conditional isVisible={index === 0}>
							<Conditional isVisible={includeSeparator}>
								<div className={classNames.anomalySummaryItemDivider} style={classStyle}></div>
							</Conditional>
						
							<CustomSection sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle} identifier={equipmentItem.equipmentNumber}>
								<div className={concatClassNames([classNames.anomalySummaryItem, "p-4 my-1"])}>
									<PDFTablePage data={[equipmentItem]} columns={equipmentDetailColumns} />

									<EquipmentAnomalyRow columns={anomalySummaryColumnsFirst} equipmentItem={equipmentItem} />
									<EquipmentAnomalyRow columns={anomalySummaryColumnsSecond} equipmentItem={equipmentItem} />
									<EquipmentAnomalyRow columns={anomalySummaryColumnsThird} equipmentItem={equipmentItem} />
									<EquipmentAnomalyRow columns={anomalySummaryColumnsFourth} equipmentItem={equipmentItem} />
									<EquipmentAnomalyRow columns={anomalySummaryColumnsFifth} equipmentItem={equipmentItem} />
									<EquipmentAnomalyRow columns={anomalySummaryColumnsSix} equipmentItem={equipmentItem} />
									<EquipmentAnomalyRow columns={anomalySummaryColumnsSeven} equipmentItem={equipmentItem} />
									<EquipmentAnomalyRow columns={anomalySummaryColumnsEight} equipmentItem={equipmentItem} />
								</div>
							</CustomSection>
						</Conditional>
					
						<Conditional isVisible={false}>
							<CustomSection sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle} identifier={equipmentItem.equipmentNumber}>
								<div className={concatClassNames([classNames.anomalySummaryItem, "p-1 my-1"])}>
									<div className={classNames.anomalySummaryItemData}>
										<div className={classNames.anomalySummaryItemDataBlock}>
											<div className={classNames.anomalySummaryItemDataBlockHeader}>Anomaly Description</div>
											<div className={classNames.anomalySummaryItemDataBlockBody}>
												{anomalyItems.map((anomaly) => <AnomalyDescription key={anomaly.id} anomaly={anomaly} />)}
											</div>
										</div>
										<div className={classNames.anomalySummaryItemDataBlock}>
											<div className={classNames.anomalySummaryItemDataBlockHeader}>Photo Ref.</div>
											<div className={concatClassNames([classNames.anomalySummaryItemDataBlockBody, 'text-center'])}>
												<span>{formatNumberRange(anomalyItems.map((anomaly) => anomaly.id))}</span>
											</div>
										</div>
									</div>
								</div>
							</CustomSection>
						</Conditional>
					</React.Fragment>
				))}
			</Conditional>

			<Conditional isVisible={paginatedAnomalyItems.length === 0}>
				<CustomSection sectionTitle={sectionTitle} sectionSubtitle={sectionSubtitle} identifier={equipmentItem.equipmentNumber}>
					<div className={concatClassNames([classNames.anomalySummaryItem, "p-1 my-1"])}>
						<Conditional isVisible={includeSeparator}>
							<div className={classNames.anomalySummaryItemDivider} style={classStyle}></div>
						</Conditional>

						<PDFTablePage data={[equipmentItem]} columns={equipmentDetailColumns} />

						<div className="my-1 table-responsive">
							<table>
								<thead>
									<tr>
										{anomalySummaryColumnsFirst.map((column) => (
											<th key={column.path}>{column.label}</th>
										))}
									</tr>
								</thead>
								<tbody>
									<tr>
										{anomalySummaryColumnsFirst.map((column: Column) => (
											<td key={column.path} style={renderStyle(equipmentItem, column)}>
												{renderCell(equipmentItem, column)}
											</td>
										))}
									</tr>
								</tbody>
							</table>
						</div>
						
						<div className="my-1 table-responsive">
							<table>
								<thead>
									<tr>
										{anomalySummaryColumnsSecond.map((column) => (
											<th key={column.path}>{column.label}</th>
										))}
									</tr>
								</thead>
								<tbody>
									<tr>
										{anomalySummaryColumnsSecond.map((column: Column) => (
											<td key={column.path} style={renderStyle(equipmentItem, column)}>
												{renderCell(equipmentItem, column)}
											</td>
										))}
									</tr>
								</tbody>
							</table>
						</div>

						<Conditional isVisible={false}>
							<div className={classNames.anomalySummaryItemData}>
								<div className={classNames.anomalySummaryItemDataBlock}>
									<div className={classNames.anomalySummaryItemDataBlockHeader}>Anomaly Description</div>
									<div className={classNames.anomalySummaryItemDataBlockBody}>
										<span>{equipmentItem.inspectionDescription}</span>
									</div>
								</div>
								<div className={classNames.anomalySummaryItemDataBlock}>
									<div className={classNames.anomalySummaryItemDataBlockHeader}>Photo Ref.</div>
									<div className={concatClassNames([classNames.anomalySummaryItemDataBlockBody, 'text-center'])}>
										<span>{formatNumberRange(equipmentItem.generalPhotos.map((p) => p.refNo))}</span>
									</div>
								</div>
							</div>
						</Conditional>
					</div>
				</CustomSection>
			</Conditional>
		</React.Fragment>
    );
};

const equipmentDetailColumns: Column[] = [
	{
		path: 'id',
		label: 'Sr. No'
	},
	{
		path: 'equipmentNumber',
		label: 'Vessel / Equipment Tag'
	},
	{
		path: 'equipmentDescription',
		label: 'Vessel / Equipment Description'
	},
];

const anomalySummaryColumnsSecond: Column[] = [
	{
		path: 'anomalySummary.flanges',
		label: 'Flanges Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.flanges)
	},
	{
		path: 'anomalySummary.fasteners',
		label: 'Fasteners Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.fasteners)
	},
	{
		path: 'anomalySummary.valves',
		label: 'Valves Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.valves)
	},
	{
		path: 'anomalySummary.corrosionUnderPipeSupport',
		label: 'Corrosion Under Support (CUS)',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.corrosionUnderPipeSupport)
	},
	{
		path: 'anomalySummary.noOfCups',
		label: 'No. of CUS (Have Anomalies)',
	},
];

const anomalySummaryColumnsThird: Column[] = [
	{
		path: 'anomalySummary.extentOfCoatingBreakdown',
		label: 'Extent of Coating Breakdown',
	},
	{
		path: 'anomalySummary.externalProtection',
		label: 'External Protection Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.externalProtection)
	},
	{
		path: 'anomalySummary.noOfExternalProtection',
		label: 'No. of Ext. Protection (Have Anomalies)',
	},
	{
		path: 'anomalySummary.supportCondition',
		label: 'Support Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.supportCondition)
	},
];

const anomalySummaryColumnsFourth: Column[] = [
	{
		path: 'anomalySummary.compositeWrapOnLine',
		label: 'Service Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.compositeWrapOnLine)
	},
	{
		path: 'anomalySummary.pipe',
		label: 'Pipe Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.pipe)
	},
	{
		path: 'anomalySummary.noOfPipe',
		label: 'No. of Pipes (Have Anomalies)',
	},
	{
		path: 'anomalySummary.guage',
		label: 'Guage Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.guage)
	},
	{
		path: 'anomalySummary.noOfGuage',
		label: 'No. of Guages (Have Anomalies)',
	},
];

const anomalySummaryColumnsFifth: Column[] = [
	{
		path: 'anomalySummary.dish',
		label: 'Dish Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.dish)
	},
	{
		path: 'anomalySummary.noOfDish',
		label: 'No. of Dishes (Have Anomalies)',
	},
	{
		path: 'anomalySummary.topDish',
		label: 'Top Dish Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.topDish)
	},
	{
		path: 'anomalySummary.noOfTopDish',
		label: 'No. of Top Dishes (Have Anomalies)',
	},
];

const anomalySummaryColumnsSix: Column[] = [
	{
		path: 'anomalySummary.bottomDish',
		label: 'Bottom Dish Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.bottomDish)
	},
	{
		path: 'anomalySummary.noOfBottomDish',
		label: 'No. of Bottom Dishes (Have Anomalies)',
	},
	{
		path: 'anomalySummary.nozzle',
		label: 'Nozzle Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.nozzle)
	},
	{
		path: 'anomalySummary.noOfNozzle',
		label: 'No. of Nozzles (Have Anomalies)',
	},
	{
		path: 'anomalySummary.namePlate',
		label: 'Name Plate Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.namePlate)
	},
	{
		path: 'anomalySummary.noOfNamePlate',
		label: 'No. of Name Plates (Have Anomalies)',
	},
];

const anomalySummaryColumnsSeven: Column[] = [
	{
		path: 'anomalySummary.skirt',
		label: 'Skirt Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.skirt)
	},
	{
		path: 'anomalySummary.noOfSkirt',
		label: 'No. of Skirts (Have Anomalies)',
	},
	{
		path: 'anomalySummary.structure',
		label: 'Structure Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.structure)
	},
	{
		path: 'anomalySummary.noOfStructure',
		label: 'No. of Structures (Have Anomalies)',
	},
];

const anomalySummaryColumnsEight: Column[] =  [
	{
		path: 'anomalySummary.insulation',
		label: 'Insulation Condition',
		style: (item) => getAnomalyFieldColor(item.anomalySummary.insulation)
	},
	{
		path: 'anomalySummary.noOfInsulation',
		label: 'No. of Insulation (Have Anomalies)',
	},
	{
		path: 'anomalySummary.others',
		label: 'Other (If any)',
	},
	{
		path: 'anomalySummary.description',
		label: 'Description',
	},
];
 
export default EquipmentAnomalySummaryItem;